import React, { useEffect, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Pill } from "../../components/common/Primitives";
import { Bell, Check, ArrowUpRight, Archive, ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { resolveNotifLink } from "../../lib/notifLinks";

function tone(kind) {
  if (!kind) return "muted";
  if (kind.startsWith("task.escalated")) return "gold";
  if (kind.startsWith("task.")) return "success";
  if (kind.startsWith("esign.signed")) return "success";
  if (kind.startsWith("esign.")) return "warn";
  if (kind.startsWith("message.")) return "gold";
  if (kind.startsWith("kyc.sla")) return "danger";
  if (kind.startsWith("kyc.")) return "warn";
  return "muted";
}

function buildKycDeepLink(meta) {
  const investorId = meta?.investor_id;
  if (investorId) return `/admin/approvals?tab=investors&entity=${investorId}`;
  const startupId = meta?.startup_id;
  if (startupId) return `/admin/approvals?tab=startups&entity=${startupId}`;
  return "/admin/approvals";
}

const PAGE_SIZE = 20;

export default function AdminNotifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [filter, setFilter] = useState("all"); // all | unread | archived
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
    if (filter === "unread") params.set("only_unread", "true");
    if (filter === "archived") params.set("include_archived", "true");
    try {
      const { data } = await api.get(`/notifications?${params.toString()}`);
      // For "archived" tab, keep only archived; the endpoint returns both when include_archived=true
      const items = filter === "archived" ? (data.items || []).filter((n) => n.archived) : (data.items || []);
      setRows(items);
      setTotal(filter === "archived" ? items.length : data.total || 0);
      setUnreadTotal(data.unread_count || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [filter]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("All marked read.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async (id) => {
    try {
      await api.post(`/notifications/${id}/archive`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveAllRead = async () => {
    try {
      const { data } = await api.post("/notifications/archive-read");
      toast.success(`Archived ${data.archived} read notification${data.archived === 1 ? "" : "s"}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <DashboardShell
      title="Notifications"
      subtitle="INBOX"
      actions={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={markAll} data-testid="notifications-mark-all-read"><Check size={14} className="inline mr-1" /> Mark all read</Button>
          <Button variant="ghost" onClick={archiveAllRead} data-testid="notifications-archive-all-read"><Archive size={14} className="inline mr-1" /> Archive read</Button>
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      <div className="inline-flex glass rounded-full p-1 mb-4" data-testid="notifications-tabs">
        <button onClick={() => setFilter("all")} data-testid="notif-tab-all" className={`px-4 py-1.5 text-sm font-heading font-semibold rounded-full transition-all ${filter === "all" ? "bg-navy text-white" : "text-ink-soft hover:text-ink"}`}>All</button>
        <button onClick={() => setFilter("unread")} data-testid="notif-tab-unread" className={`px-4 py-1.5 text-sm font-heading font-semibold rounded-full transition-all ${filter === "unread" ? "bg-navy text-white" : "text-ink-soft hover:text-ink"}`}>Unread {unreadTotal > 0 && `· ${unreadTotal}`}</button>
        <button onClick={() => setFilter("archived")} data-testid="notif-tab-archived" className={`px-4 py-1.5 text-sm font-heading font-semibold rounded-full transition-all ${filter === "archived" ? "bg-navy text-white" : "text-ink-soft hover:text-ink"}`}>Archived</button>
      </div>

      <div className="space-y-2" data-testid="notifications-list">
        {loading && rows.length === 0 && <div className="text-ink-muted text-sm">Loading…</div>}
        {!loading && rows.length === 0 && <div className="glass rounded-lg p-10 text-center text-ink-muted" data-testid="notifications-empty"><Bell size={32} className="mx-auto mb-3 text-ink-faint" />No notifications.</div>}
        {rows.map((n) => {
          const id = n.notification_id || n.notif_id;
          const kindLabel = (n.kind || n.type || "notice").replace(".", " · ");
          const linkTo = resolveNotifLink(n, role);
          const openRow = async () => {
            if (!n.read) {
              // fire-and-forget mark-read so navigation is instant
              api.post(`/notifications/${id}/read`).catch(() => {});
            }
            if (linkTo) navigate(linkTo);
          };
          return (
            <div
              key={id}
              onClick={openRow}
              role={linkTo ? "link" : undefined}
              tabIndex={linkTo ? 0 : undefined}
              onKeyDown={(e) => { if (linkTo && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openRow(); } }}
              className={`glass rounded-md p-4 flex items-start gap-3 transition-all ${
                linkTo ? "cursor-pointer hover:ring-1 hover:ring-emerald-400/40" : ""
              } ${n.archived ? "opacity-50" : n.read ? "opacity-70" : "border-l-4 border-l-gold"}`}
              data-testid={`notif-row-${id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Pill tone={tone(n.kind || n.type)}>{kindLabel}</Pill>
                  {!n.read && !n.archived && <span className="text-[10px] font-mono text-gold uppercase tracking-widest">NEW</span>}
                  {n.archived && <span className="text-[10px] font-mono text-ink-muted uppercase tracking-widest">ARCHIVED</span>}
                  <span className="text-[10px] font-mono text-ink-muted ml-auto">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="font-heading text-ink text-sm">{n.title || n.body || "Notification"}</div>
                {n.body && n.title && <div className="text-xs text-ink-soft mt-0.5 line-clamp-3">{n.body}</div>}
                {n.meta?.reason && <div className="text-xs text-ink-muted italic mt-1">Reason: &ldquo;{n.meta.reason}&rdquo;</div>}
                {n.meta?.by && <div className="text-[10px] font-mono text-ink-muted mt-1">by {n.meta.by}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                {linkTo && (
                  <Link
                    to={linkTo}
                    onClick={() => { if (!n.read) api.post(`/notifications/${id}/read`).catch(() => {}); }}
                    className="p-1.5 text-[#8A6600] hover:bg-gold/10 rounded"
                    title="Open"
                    data-testid={`notif-open-${id}`}
                  >
                    <ArrowUpRight size={16} />
                  </Link>
                )}
                {!n.read && !n.archived && (
                  <button onClick={() => markRead(id)} className="p-1.5 text-emerald-700 hover:bg-emerald-500/10 rounded" title="Mark read" data-testid={`notif-mark-${id}`}>
                    <Check size={16} />
                  </button>
                )}
                {!n.archived && (
                  <button onClick={() => archive(id)} className="p-1.5 text-ink-muted hover:bg-navy/[0.05] hover:text-ink rounded" title="Archive" data-testid={`notif-archive-${id}`}>
                    <Archive size={16} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {total > PAGE_SIZE && filter !== "archived" && (
        <div className="flex items-center justify-between mt-6" data-testid="notifications-pagination">
          <div className="text-xs font-mono text-ink-muted">
            Page {currentPage} of {totalPages} · {total} total
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="px-3 py-1.5 border border-navy/15 rounded-md text-sm font-heading disabled:opacity-40 hover:bg-navy/[0.04]"
              data-testid="notifications-prev-page"
            >
              <ChevronLeft size={14} className="inline" /> Prev
            </button>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="px-3 py-1.5 border border-navy/15 rounded-md text-sm font-heading disabled:opacity-40 hover:bg-navy/[0.04]"
              data-testid="notifications-next-page"
            >
              Next <ChevronRight size={14} className="inline" />
            </button>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
